'use client';

import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus, Trash2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useReceiptBuilder } from '@/hooks/useReceiptBuilder';
import { SECTION_METADATA, type Alignment, type DividerStyle, type BarcodeType } from '@/lib/templates/schema';

const ALIGNMENT_OPTIONS: { value: Alignment; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const DIVIDER_OPTIONS: { value: DividerStyle; label: string; icon: string }[] = [
  { value: 'dashed', label: 'Dashed', icon: '---' },
  { value: 'equals', label: 'Equals', icon: '===' },
  { value: 'dots', label: 'Dots', icon: '...' },
  { value: 'colons', label: 'Colons', icon: ':::' },
  { value: 'stars', label: 'Stars', icon: '***' },
  { value: 'spaces', label: 'Blank', icon: '   ' },
];

const BARCODE_OPTIONS: { value: BarcodeType; label: string }[] = [
  { value: 'code128', label: 'Code 128' },
  { value: 'code39', label: 'Code 39' },
  { value: 'ean13', label: 'EAN-13' },
  { value: 'upc', label: 'UPC' },
  { value: 'qr', label: 'QR Code' },
];

const QR_DETAIL_OPTIONS = [
  { value: 'simple', label: 'Simple', length: 8 },
  { value: 'medium', label: 'Medium', length: 16 },
  { value: 'detailed', label: 'Detailed', length: 32 },
];

// Barcode format validation
function validateBarcode(type: BarcodeType, value: string): { valid: boolean; message: string } {
  if (!value) {
    return { valid: false, message: 'Value is required' };
  }

  switch (type) {
    case 'ean13':
      if (!/^\d+$/.test(value)) {
        return { valid: false, message: 'EAN-13 requires numeric digits only' };
      }
      if (value.length !== 12 && value.length !== 13) {
        return { valid: false, message: `EAN-13 requires exactly 12-13 digits (currently ${value.length})` };
      }
      return { valid: true, message: '' };

    case 'upc':
      if (!/^\d+$/.test(value)) {
        return { valid: false, message: 'UPC requires numeric digits only' };
      }
      if (value.length !== 11 && value.length !== 12) {
        return { valid: false, message: `UPC requires exactly 11-12 digits (currently ${value.length})` };
      }
      return { valid: true, message: '' };

    case 'code39':
      if (!/^[0-9A-Z\-. $/+%]*$/i.test(value)) {
        return { valid: false, message: 'Code 39 only supports: 0-9, A-Z, -, ., space, $, /, +, %' };
      }
      return { valid: true, message: '' };

    case 'code128':
      // Code 128 supports most ASCII characters
      return { valid: true, message: '' };

    default:
      return { valid: true, message: '' };
  }
}

function AlignmentSelector({
  value,
  onChange,
}: {
  value: Alignment;
  onChange: (value: Alignment) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>Alignment</Label>
      <div className="flex gap-1">
        {ALIGNMENT_OPTIONS.map((option) => (
          <Button
            key={option.value}
            variant={value === option.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => onChange(option.value)}
            className="flex-1"
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function DividerToggle({
  show,
  style,
  onShowChange,
  onStyleChange,
}: {
  show: boolean;
  style: DividerStyle;
  onShowChange: (show: boolean) => void;
  onStyleChange: (style: DividerStyle) => void;
}) {
  return (
    <div className="space-y-3 pt-4 border-t">
      <div className="flex items-center justify-between">
        <Label>Divider at the bottom</Label>
        <Switch checked={show} onCheckedChange={onShowChange} />
      </div>
      {show && (
        <div className="flex gap-1">
          {DIVIDER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onStyleChange(option.value)}
              className={`flex-1 h-8 px-2 rounded border flex items-center justify-center transition-colors ${
                style === option.value 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-background text-muted-foreground border-input hover:bg-accent'
              }`}
              title={option.label}
            >
              <span className="font-mono text-sm">{option.icon}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const WHITESPACE_HINT_KEY = 'receipt-builder-whitespace-hint-dismissed';

// Whitespace hint component
function WhitespaceHint({ show, onDismiss }: { show: boolean; onDismiss: () => void }) {
  if (!show) return null;
  return (
    <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-1.5 rounded border border-dashed flex items-center justify-between gap-2">
      <span>💡 Spaces are preserved — add leading/trailing spaces for alignment</span>
      <button
        onClick={onDismiss}
        className="text-muted-foreground hover:text-foreground shrink-0"
        aria-label="Dismiss hint"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export function SectionEditor() {
  const { template, selectedSectionId, updateSection, selectSection } = useReceiptBuilder();
  const [showWhitespaceHint, setShowWhitespaceHint] = useState(true);

  // Check localStorage on mount
  useEffect(() => {
    const dismissed = localStorage.getItem(WHITESPACE_HINT_KEY);
    if (dismissed === 'true') {
      setShowWhitespaceHint(false);
    }
  }, []);

  const dismissHint = () => {
    setShowWhitespaceHint(false);
    localStorage.setItem(WHITESPACE_HINT_KEY, 'true');
  };

  const section = template.sections.find((s) => s.id === selectedSectionId);

  if (!section) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="text-sm">Select a section to edit its properties.</p>
      </div>
    );
  }

  const meta = SECTION_METADATA.find((m) => m.type === section.type);

  const update = (config: Record<string, unknown>) => {
    updateSection(section.id, config);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 pb-2 border-b">
        <span className="text-xl">{meta?.icon}</span>
        <h3 className="font-semibold">{meta?.label}</h3>
      </div>

      {/* Header Section Editor */}
      {section.type === 'header' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Logo (Upload or URL)</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    update({ logoData: reader.result as string, logoUrl: '' });
                  };
                  reader.readAsDataURL(file);
                }
              }}
            />
            <Input
              value={section.config.logoUrl || ''}
              onChange={(e) => update({ logoUrl: e.target.value, logoData: '' })}
              placeholder="Or paste URL..."
            />
            {(section.config.logoData || section.config.logoUrl) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => update({ logoData: '', logoUrl: '' })}
              >
                Clear Logo
              </Button>
            )}
          </div>

          {(section.config.logoData || section.config.logoUrl) && (
            <div className="space-y-2">
              <Label>Logo Size</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-muted-foreground">Width: {section.config.logoWidth}px</span>
                  <Slider
                    min={40}
                    max={200}
                    step={10}
                    value={[section.config.logoWidth]}
                    onValueChange={([value]) => update({ logoWidth: value })}
                  />
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Height: {section.config.logoHeight}px</span>
                  <Slider
                    min={20}
                    max={100}
                    step={5}
                    value={[section.config.logoHeight]}
                    onValueChange={([value]) => update({ logoHeight: value })}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Business Name</Label>
            <Input
              value={section.config.businessName}
              onChange={(e) => update({ businessName: e.target.value })}
              className="font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label>Business Name Font Size</Label>
            <Slider
              min={12}
              max={32}
              step={1}
              value={[section.config.businessNameFontSize]}
              onValueChange={([value]) => update({ businessNameFontSize: value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Address Lines</Label>
            <Textarea
              value={section.config.addressLines.join('\n')}
              onChange={(e) =>
                update({ addressLines: e.target.value.split('\n') })
              }
              rows={4}
              placeholder="Line 1&#10;Line 2&#10;Line 3"
              className="font-mono"
            />
          </div>

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* Two Column Section Editor */}
      {section.type === 'two-column' && (
        <div className="space-y-4">
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          {/* Column 1 */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Column 1</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  update({
                    column1: [
                      ...section.config.column1,
                      { id: uuidv4(), label: 'Label', value: 'Value' },
                    ],
                  })
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {section.config.column1.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-center">
                <Input
                  value={item.label}
                  onChange={(e) => {
                    const column1 = [...section.config.column1];
                    column1[index] = { ...item, label: e.target.value };
                    update({ column1 });
                  }}
                  placeholder="Label"
                  className="flex-1 font-mono"
                />
                <Input
                  value={item.value}
                  onChange={(e) => {
                    const column1 = [...section.config.column1];
                    column1[index] = { ...item, value: e.target.value };
                    update({ column1 });
                  }}
                  placeholder="Value"
                  className="flex-1 font-mono"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive shrink-0"
                  onClick={() => {
                    update({
                      column1: section.config.column1.filter((r) => r.id !== item.id),
                    });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Column 2 */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Column 2</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  update({
                    column2: [
                      ...section.config.column2,
                      { id: uuidv4(), label: 'Label', value: 'Value' },
                    ],
                  })
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>
            {section.config.column2.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-center">
                <Input
                  value={item.label}
                  onChange={(e) => {
                    const column2 = [...section.config.column2];
                    column2[index] = { ...item, label: e.target.value };
                    update({ column2 });
                  }}
                  placeholder="Label"
                  className="flex-1 font-mono"
                />
                <Input
                  value={item.value}
                  onChange={(e) => {
                    const column2 = [...section.config.column2];
                    column2[index] = { ...item, value: e.target.value };
                    update({ column2 });
                  }}
                  placeholder="Value"
                  className="flex-1 font-mono"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive shrink-0"
                  onClick={() => {
                    update({
                      column2: section.config.column2.filter((r) => r.id !== item.id),
                    });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* Items List Section Editor */}
      {section.type === 'items-list' && (
        <div className="space-y-4">
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <div className="flex items-center justify-between">
            <Label>Show Quantity Column</Label>
            <Switch
              checked={section.config.showQuantity}
              onCheckedChange={(checked) => update({ showQuantity: checked })}
            />
          </div>

          {section.config.showQuantity && (
            <div className="space-y-2">
              <Label>Quantity Position</Label>
              <div className="flex gap-2">
                <Button
                  variant={section.config.quantityPosition !== 'right' ? 'default' : 'outline'}
                  onClick={() => update({ quantityPosition: 'left' })}
                  className="flex-1"
                >
                  Left (Qty Item)
                </Button>
                <Button
                  variant={section.config.quantityPosition === 'right' ? 'default' : 'outline'}
                  onClick={() => update({ quantityPosition: 'right' })}
                  className="flex-1"
                >
                  Right (Item Qty)
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <Label>Show Header Row</Label>
            <Switch
              checked={section.config.showHeader}
              onCheckedChange={(checked) => update({ showHeader: checked })}
            />
          </div>

          {section.config.showHeader && (
            <div className="flex items-center justify-between">
              <Label>Header Separator Line</Label>
              <Switch
                checked={section.config.showHeaderDivider}
                onCheckedChange={(checked) => update({ showHeaderDivider: checked })}
              />
            </div>
          )}

          {section.config.showHeader && (
            <div className="space-y-2">
              <Label>Header Labels</Label>
              <div className="flex gap-2">
                {section.config.showQuantity && (
                  <Input
                    value={section.config.headerLabels?.quantity || ''}
                    onChange={(e) => update({ headerLabels: { ...section.config.headerLabels, quantity: e.target.value } })}
                    placeholder="Qty"
                    className="w-20"
                  />
                )}
                <Input
                  value={section.config.headerLabels?.item || ''}
                  onChange={(e) => update({ headerLabels: { ...section.config.headerLabels, item: e.target.value } })}
                  placeholder="Item"
                  className="flex-1"
                />
                <Input
                  value={section.config.headerLabels?.amount || ''}
                  onChange={(e) => update({ headerLabels: { ...section.config.headerLabels, amount: e.target.value } })}
                  placeholder="Amount"
                  className="w-24"
                />
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Items</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  update({
                    items: [
                      ...section.config.items,
                      { id: uuidv4(), quantity: '1', name: 'New Item', price: '$0.00' },
                    ],
                  })
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add Item
              </Button>
            </div>
            {section.config.items.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-start">
                {section.config.showQuantity && (
                  <Input
                    value={item.quantity || ''}
                    onChange={(e) => {
                      const items = [...section.config.items];
                      items[index] = { ...item, quantity: e.target.value };
                      update({ items });
                    }}
                    placeholder="Qty"
                    className="w-20 font-mono"
                  />
                )}
                <Input
                  value={item.name}
                  onChange={(e) => {
                    const items = [...section.config.items];
                    items[index] = { ...item, name: e.target.value };
                    update({ items });
                  }}
                  placeholder="Item name"
                  className="flex-1 font-mono"
                />
                <Input
                  value={item.price}
                  onChange={(e) => {
                    const items = [...section.config.items];
                    items[index] = { ...item, price: e.target.value };
                    update({ items });
                  }}
                  placeholder="$0.00"
                  className="w-24 font-mono"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive shrink-0"
                  onClick={() => {
                    update({
                      items: section.config.items.filter((i) => i.id !== item.id),
                    });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* Totals Section Editor */}
      {section.type === 'totals' && (
        <div className="space-y-4">
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <div className="space-y-2">
            <Label>Label Position: {section.config.labelPosition ?? 50}%</Label>
            <Slider
              min={0}
              max={100}
              step={5}
              value={[section.config.labelPosition ?? 50]}
              onValueChange={([value]) => update({ labelPosition: value })}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>← Left</span>
              <span>Right (overlap) →</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Label Alignment</Label>
            <Select
              value={section.config.labelAlignment || 'left'}
              onValueChange={(value: 'left' | 'center' | 'right') => update({ labelAlignment: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label>Increase Last Line Size</Label>
            <Switch
              checked={section.config.increaseTotalSize}
              onCheckedChange={(checked) => update({ increaseTotalSize: checked })}
            />
          </div>

          {section.config.increaseTotalSize && (
            <div className="flex gap-1">
              {[10, 20, 30, 40, 50].map((size) => (
                <Button
                  key={size}
                  variant={section.config.totalSizeIncrease === size ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => update({ totalSizeIncrease: size })}
                  className="flex-1"
                >
                  +{size}%
                </Button>
              ))}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label>Lines</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  update({
                    lines: [
                      ...section.config.lines,
                      { id: uuidv4(), label: 'Label', value: 0, isBold: false, isLarge: false },
                    ],
                  })
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add Line
              </Button>
            </div>
            {section.config.lines.map((line, index) => (
              <div key={line.id} className="flex gap-2 items-start">
                <Input
                  value={line.label}
                  onChange={(e) => {
                    const lines = [...section.config.lines];
                    lines[index] = { ...line, label: e.target.value };
                    update({ lines });
                  }}
                  placeholder="Label"
                  className="flex-1 font-mono"
                />
                <Input
                  type="text"
                  value={line.value}
                  onChange={(e) => {
                    const lines = [...section.config.lines];
                    lines[index] = { ...line, value: e.target.value };
                    update({ lines });
                  }}
                  className="w-24 font-mono"
                />
                <Button
                  variant={line.isInverted ? 'default' : 'outline'}
                  size="icon"
                  className="shrink-0"
                  title="Invert colors (black bg, white text)"
                  onClick={() => {
                    const lines = [...section.config.lines];
                    lines[index] = { ...line, isInverted: !line.isInverted };
                    update({ lines });
                  }}
                >
                  <span className="text-xs font-bold">▣</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-destructive shrink-0"
                  onClick={() => {
                    update({
                      lines: section.config.lines.filter((l) => l.id !== line.id),
                    });
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* Payment Section Editor */}
      {section.type === 'payment' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Payment Type</Label>
            <div className="flex gap-2">
              <Button
                variant={section.config.paymentType === 'card' ? 'default' : 'outline'}
                onClick={() => update({ paymentType: 'card' })}
                className="flex-1"
              >
                💳 Card
              </Button>
              <Button
                variant={section.config.paymentType === 'cash' ? 'default' : 'outline'}
                onClick={() => update({ paymentType: 'cash' })}
                className="flex-1"
              >
                💵 Cash
              </Button>
            </div>
          </div>

          {/* Card Fields */}
          {section.config.paymentType === 'card' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Card Fields</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    update({
                      cardFields: [
                        ...(section.config.cardFields || []),
                        { id: uuidv4(), label: 'Field', value: 'Value' },
                      ],
                    })
                  }
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Field
                </Button>
              </div>
              {(section.config.cardFields || []).map((field, index) => (
                <div key={field.id} className="flex gap-2 items-start">
                  <Input
                    value={field.label}
                    onChange={(e) => {
                      const cardFields = [...(section.config.cardFields || [])];
                      cardFields[index] = { ...field, label: e.target.value };
                      update({ cardFields });
                    }}
                    placeholder="Label"
                    className="flex-1 font-mono"
                  />
                  <Input
                    value={field.value}
                    onChange={(e) => {
                      const cardFields = [...(section.config.cardFields || [])];
                      cardFields[index] = { ...field, value: e.target.value };
                      update({ cardFields });
                    }}
                    placeholder="Value"
                    className="flex-1 font-mono"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive shrink-0"
                    onClick={() => {
                      update({
                        cardFields: (section.config.cardFields || []).filter((f) => f.id !== field.id),
                      });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Cash Fields */}
          {section.config.paymentType === 'cash' && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Cash Fields</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    update({
                      cashFields: [
                        ...(section.config.cashFields || []),
                        { id: uuidv4(), label: 'Field', value: 'Value' },
                      ],
                    })
                  }
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Field
                </Button>
              </div>
              {(section.config.cashFields || []).map((field, index) => (
                <div key={field.id} className="flex gap-2 items-start">
                  <Input
                    value={field.label}
                    onChange={(e) => {
                      const cashFields = [...(section.config.cashFields || [])];
                      cashFields[index] = { ...field, label: e.target.value };
                      update({ cashFields });
                    }}
                    placeholder="Label"
                    className="flex-1 font-mono"
                  />
                  <Input
                    value={field.value}
                    onChange={(e) => {
                      const cashFields = [...(section.config.cashFields || [])];
                      cashFields[index] = { ...field, value: e.target.value };
                      update({ cashFields });
                    }}
                    placeholder="Value"
                    className="flex-1 font-mono"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive shrink-0"
                    onClick={() => {
                      update({
                        cashFields: (section.config.cashFields || []).filter((f) => f.id !== field.id),
                      });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* DateTime Section Editor */}
      {section.type === 'datetime' && (
        <div className="space-y-4">
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <AlignmentSelector
            value={section.config.alignment}
            onChange={(alignment) => update({ alignment })}
          />

          <div className="space-y-2">
            <Label>Date/Time Value</Label>
            <Input
              value={section.config.dateTime}
              onChange={(e) => update({ dateTime: e.target.value })}
              className="font-mono"
            />
          </div>

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* Custom Message Section Editor */}
      {section.type === 'custom-message' && (
        <div className="space-y-4">
          <WhitespaceHint show={showWhitespaceHint} onDismiss={dismissHint} />

          <AlignmentSelector
            value={section.config.alignment}
            onChange={(alignment) => update({ alignment })}
          />

          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={section.config.message}
              onChange={(e) => update({ message: e.target.value })}
              rows={5}
              className="font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label>Font Size (px)</Label>
            <Slider
              min={8}
              max={16}
              step={1}
              value={[section.config.fontSize]}
              onValueChange={([value]) => update({ fontSize: value })}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label>Bold</Label>
            <Switch
              checked={section.config.isBold ?? false}
              onCheckedChange={(checked) => update({ isBold: checked })}
            />
          </div>

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* Barcode Section Editor */}
      {section.type === 'barcode' && (
        <div className="space-y-4">
          <AlignmentSelector
            value={section.config.alignment}
            onChange={(alignment) => update({ alignment })}
          />

          <div className="space-y-2">
            <Label>Barcode Type</Label>
            <Select
              value={section.config.type}
              onValueChange={(value: BarcodeType) => {
                // When switching to QR, set a default detail level value and size
                if (value === 'qr') {
                  update({ type: value, value: 'QR_MEDIUM', showValue: false, height: 100, width: 2 });
                } else {
                  // When switching from QR to other types, reset to default barcode value and size
                  const currentValue = section.config.value;
                  const isQRValue = currentValue?.startsWith('QR_');
                  const wasQR = section.config.type === 'qr';
                  if (isQRValue || wasQR) {
                    update({ type: value, value: '123456789012', height: 50, width: 2 });
                  } else {
                    update({ type: value });
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BARCODE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* QR Code Detail Level */}
          {section.config.type === 'qr' ? (
            <div className="space-y-2">
              <Label>Detail Level</Label>
              <Select
                value={section.config.value?.startsWith('QR_') ? section.config.value : 'QR_MEDIUM'}
                onValueChange={(value) => update({ value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="QR_SIMPLE">Simple (8 chars)</SelectItem>
                  <SelectItem value="QR_MEDIUM">Medium (16 chars)</SelectItem>
                  <SelectItem value="QR_DETAILED">Detailed (32 chars)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Generates a random QR code with varying complexity
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Value</Label>
              <Input
                value={section.config.value}
                onChange={(e) => update({ value: e.target.value })}
              />
              {(() => {
                const validation = validateBarcode(section.config.type, section.config.value);
                if (!validation.valid) {
                  return (
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                      ⚠️ {validation.message}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          )}

          {/* Show Value Below - only for non-QR barcodes */}
          {section.config.type !== 'qr' && (
            <div className="flex items-center justify-between">
              <Label>Show Value Below</Label>
              <Switch
                checked={section.config.showValue}
                onCheckedChange={(checked) => update({ showValue: checked })}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Size</Label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted-foreground">{section.config.type === 'qr' ? 'QR Size' : 'Width'}</span>
                <Slider
                  min={section.config.type === 'qr' ? 60 : 0.5}
                  max={section.config.type === 'qr' ? 150 : 3}
                  step={section.config.type === 'qr' ? 10 : 0.1}
                  value={[section.config.type === 'qr' ? section.config.height : section.config.width]}
                  onValueChange={([value]) => update(section.config.type === 'qr' ? { height: value } : { width: value })}
                />
              </div>
              {section.config.type !== 'qr' && (
                <div>
                  <span className="text-xs text-muted-foreground">Height</span>
                  <Slider
                    min={15}
                    max={100}
                    step={5}
                    value={[section.config.height]}
                    onValueChange={([value]) => update({ height: value })}
                  />
                </div>
              )}
            </div>
          </div>

          <DividerToggle
            show={section.config.showDivider}
            style={section.config.dividerStyle}
            onShowChange={(show) => update({ showDivider: show })}
            onStyleChange={(style) => update({ dividerStyle: style })}
          />
        </div>
      )}

      {/* Divider Section Editor */}
      {section.type === 'divider' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Style</Label>
            <div className="flex gap-1">
              {DIVIDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => update({ style: option.value })}
                  className={`flex-1 h-7 px-2 rounded border flex items-center justify-center ${
                    section.config.style === option.value 
                      ? 'bg-primary text-primary-foreground border-primary' 
                      : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
                  title={option.label}
                >
                  <span className="font-mono text-xs">{option.icon}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Spacing (px)</Label>
            <Slider
              min={4}
              max={24}
              step={2}
              value={[section.config.spacing]}
              onValueChange={([value]) => update({ spacing: value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}

